import { useEffect, useState } from "react";
import { Truck } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

/**
 * Ship dialog — TRACKING IS OPTIONAL. Shipping proceeds with or without
 * tracking data. The dialog only offers the fields so admins can attach
 * carrier/number/URL at ship time.
 *
 * Also used to edit tracking POST-ship (title/CTA change based on `mode`).
 */
export default function ShipDialog({
  open,
  onOpenChange,
  mode = "ship", // "ship" | "edit-tracking"
  initial = null,
  submitting = false,
  onSubmit, // (payload) => Promise<void>
}) {
  const [tn, setTn] = useState("");
  const [carrier, setCarrier] = useState("");
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (!open) return;
    setTn(initial?.tracking_number || "");
    setCarrier(initial?.tracking_carrier || "");
    setUrl(initial?.tracking_url || "");
  }, [open, initial]);

  const submit = async () => {
    const payload = {
      tracking_number: tn.trim() || null,
      tracking_carrier: carrier.trim() || null,
      tracking_url: url.trim() || null,
    };
    await onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ship-dialog">
        <DialogHeader>
          <DialogTitle className="db-h inline-flex items-center gap-2">
            <Truck size={16} /> {mode === "edit-tracking" ? "Edit tracking" : "Mark order as shipped"}
          </DialogTitle>
          <DialogDescription>
            {mode === "edit-tracking"
              ? "Update the carrier or tracking number. Leave any field blank to clear it."
              : "Tracking is optional — leave blank if you don't have a number yet. You can add or edit tracking after shipping."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-2">
          <div>
            <label className="text-xs text-[#5C665A]">Tracking number <span className="opacity-70">(optional)</span></label>
            <Input
              data-testid="ship-tracking-number"
              value={tn}
              onChange={(e) => setTn(e.target.value)}
              className="h-9 db-mono"
              placeholder="e.g. LX000123456NL"
            />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Carrier <span className="opacity-70">(optional)</span></label>
            <Input
              data-testid="ship-tracking-carrier"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="h-9"
              placeholder="DHL, PostNL, UPS…"
            />
          </div>
          <div>
            <label className="text-xs text-[#5C665A]">Tracking URL <span className="opacity-70">(optional)</span></label>
            <Input
              data-testid="ship-tracking-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="h-9"
              placeholder="https://…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ship-dialog-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
            data-testid="ship-dialog-submit"
          >
            <Truck size={14} /> {mode === "edit-tracking" ? "Save tracking" : (tn.trim() ? "Ship & save tracking" : "Ship without tracking")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
